import React from 'react';
import {
    AddCircle16Filled,
    SubtractCircle16Regular,
    Star16Regular,
    Star16Filled,
    CommentAdd16Regular,
    Comment16Filled,
    ErrorCircle16Regular,
    Checkmark16Regular,
    ArrowMaximize16Regular,
    MoreVertical16Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Tooltip } from '@fluentui/react-components';
import { CustomAvatar } from './index';
import TagManagementMenu from './TagManagementMenu';
import WorkstreamManagementMenu from './WorkstreamManagementMenu';
import DeadlinePickerMenu from './TimelinePickerMenu';
import TimelineRangePickerMenu from './TimelineRangePickerMenu';
import UserAssignmentMenu from './UserAssignmentMenu';
import styles from './css/index.module.css';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const HierarchicalTaskRow = ({
    task,
    index,
    editingTaskId,
    setEditingTaskId,
    editingTaskText,
    setEditingTaskText,
    handleTaskTextClick,
    handleTaskTextSave,
    handleTaskTextCancel,
    handleTaskTextKeyPress,
    handleStarClick,
    handleUserAssignment,
    handleDeadlineSelect,
    handleStatusChange,
    handlePriorityChange,
    handleTagSelect,
    handleWorkstreamSelect,
    handleEdit,
    handleDelete,
    handleSelectRow,
    selectedRows,
    isColumnVisible,
    columnWidths,
    columnOrder,
    projectUsersAll,
    filteredData,
    onOpenSidebar,
    getUserInfo,
    getStatusInfo,
    getPriorityInfo,
    formatLastUpdated,
    formatDeadline,
    level = 0,
    isExpanded = false,
    onToggleExpanded,
    onAddChildTask,
    expandedTasks,
    handleProjectPhaseChange,
}) => {
    // Inline edit state for Project Phase column
    const [isEditingProjectPhase, setIsEditingProjectPhase] = React.useState(false);
    const [projectPhaseDraft, setProjectPhaseDraft] = React.useState(task.project_phase || '');

    // Get child tasks for this task - need to get from all tasks, not just filtered data
    const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];
    const childTasks = allTasks.filter(t => t.parent_task === task.id);
    const hasChildren = childTasks.length > 0;

    // Make row draggable/sortable
    // Only make root tasks and expanded parent tasks droppable
    const isDroppable = level === 0 || (level > 0 && expandedTasks?.has ? expandedTasks.has(task.parent_task) : false);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: String(task.id),
        disabled: !isDroppable,
        data: {
            type: 'task',
            task: task,
            level: level,
        },
    });

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    // Check if task can have children (allow up to 3 total levels: 0,1,2)
    // Level 0: Root tasks (can have children)
    // Level 1: First-level children (can have children)
    // Level 2: Second-level children (CANNOT have children)
    const canHaveChildren = level < 2;

    // Background color based on level
    const getBackgroundColor = () => {
        if (level === 1) return '#F2F6FE';
        if (level === 2) return '#EAF1FE';
        return 'white'; // Top level tasks should have white background
    };

    // Indentation based on level
    const getIndentation = () => {
        return level * 24; // 24px per level
    };

    const formatTimelineDate = dateStr => {
        try {
            if (!dateStr) return '-- --';
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return '-- --';
            return d.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
            });
        } catch {
            return '-- --';
        }
    };

    const getDurationInDays = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) return null;
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) return null;
        const dayMs = 1000 * 60 * 60 * 24;
        return Math.floor(diffMs / dayMs);
    };

    const formatDurationLabel = (startDateStr, endDateStr) => {
        const days = getDurationInDays(startDateStr, endDateStr);
        if (days == null) return '-- --';

        if (days < 30) {
            return `${days} day${days === 1 ? '' : 's'}`;
        }

        if (days < 365) {
            const months = Math.floor(days / 30);
            const remainingDays = days - months * 30;
            if (remainingDays === 0) {
                return `${months} month${months === 1 ? '' : 's'}`;
            }
            return `${months} month${months === 1 ? '' : 's'} ${remainingDays} day${remainingDays === 1 ? '' : 's'}`;
        }

        const years = Math.floor(days / 365);
        const remainingDays = days - years * 365;
        const months = Math.floor(remainingDays / 30);
        if (months === 0) {
            return `${years} year${years === 1 ? '' : 's'}`;
        }
        return `${years} year${years === 1 ? '' : 's'} ${months} month${months === 1 ? '' : 's'}`;
    };

    // Handle adding child task
    const handleAddChildClick = e => {
        e.stopPropagation();
        // Always toggle expansion. For tasks without children, this opens/closes the add-child row.
        onToggleExpanded(task.id);
    };

    return (
        <>
            {/* Main task row */}
            <tr
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                className={styles.tableRow}
                style={{
                    ...sortableStyle,
                    backgroundColor: getBackgroundColor(),
                }}
                onMouseEnter={e => {
                    const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                    if (checkbox && !selectedRows.includes(task.id)) {
                        checkbox.style.opacity = '1';
                    }

                    // Show ArrowMaximize, Star, Menu, and Add icons on hover
                    const arrowIcon = e.currentTarget.querySelector('.hover-icon');
                    const starIcon = e.currentTarget.querySelector('[data-icon-type="star"]');
                    const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                    const addIcon = e.currentTarget.querySelector('.hover-only-icon');
                    if (arrowIcon) arrowIcon.style.display = 'flex';
                    if (starIcon) starIcon.style.opacity = '1';
                    if (menuIcon) menuIcon.style.display = 'flex';
                    if (addIcon) addIcon.style.opacity = '1';
                }}
                onMouseLeave={e => {
                    const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                    if (checkbox && !selectedRows.includes(task.id)) {
                        checkbox.style.opacity = '0';
                    }

                    // Hide ArrowMaximize icon on leave
                    const arrowIcon = e.currentTarget.querySelector('.hover-icon');
                    if (arrowIcon) arrowIcon.style.display = 'none';

                    // Hide Star icon only if task is not starred
                    const starIcon = e.currentTarget.querySelector('[data-icon-type="star"]');
                    if (starIcon && !task.starred) {
                        starIcon.style.opacity = '0';
                    }

                    // Hide Menu icon on leave
                    const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                    if (menuIcon) menuIcon.style.display = 'none';

                    // Hide Add icon on leave
                    const addIcon = e.currentTarget.querySelector('.hover-only-icon');
                    if (addIcon) addIcon.style.opacity = '0';
                }}>
                {/* Checkbox column */}
                <td className={styles.tableRowItem} style={{ width: columnWidths.checkbox }}>
                    <input
                        className={styles.checkboxTable}
                        type="checkbox"
                        checked={selectedRows.includes(task.id)}
                        onChange={() => handleSelectRow(task.id)}
                        style={{
                            opacity: selectedRows.includes(task.id) ? 1 : 0,
                            transition: 'opacity 0.2s ease',
                        }}
                    />
                </td>

                {/* Dynamic columns based on order (includes task) */}
                {(columnOrder || ['task', 'owner', 'status', 'timeline', 'deadline', 'duration', 'priority', 'workstream', 'tag', 'projectPhase', 'lastUpdated', 'addedOn'])
                    .filter(col => isColumnVisible(col))
                    .map(col => {
                        if (col === 'task') {
                            return (
                                <td
                                    key={col}
                                    className={styles.tableRowItem}
                                    style={{ width: columnWidths.task }}
                                    onMouseEnter={e => {
                                        const icon = e.currentTarget.querySelector('.hover-icon');
                                        if (icon) icon.style.display = 'flex';
                                    }}
                                    onMouseLeave={e => {
                                        const icon = e.currentTarget.querySelector('.hover-icon');
                                        if (icon) icon.style.display = 'none';
                                    }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center" style={{ paddingLeft: getIndentation(), minWidth: 0, flex: '1 1 auto' }}>
                                            {canHaveChildren ? (
                                                <div className="me-2" style={{ cursor: 'pointer', minWidth: '16px', marginLeft: '8px' }} onClick={handleAddChildClick}>
                                                    {hasChildren ? (
                                                        isExpanded ? (
                                                            <SubtractCircle16Regular style={{ color: 'var(--brand-secondary, #8E2DE2)' }} />
                                                        ) : (
                                                            <AddCircle16Filled style={{ color: 'var(--brand-secondary, #8E2DE2)' }} />
                                                        )
                                                    ) : isExpanded ? (
                                                        <SubtractCircle16Regular style={{ color: 'var(--brand-secondary, #8E2DE2)' }} />
                                                    ) : (
                                                        <AddCircle16Filled className="hover-only-icon" style={{ color: '#C0C6D8', opacity: 0, transition: 'opacity 0.2s ease' }} />
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="me-2" style={{ minWidth: '16px', marginLeft: '8px' }} />
                                            )}
                                            <div className="flex-fill" style={{ minWidth: 0 }}>
                                                {editingTaskId === task.id ? (
                                                    <input
                                                        type="text"
                                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                                        value={editingTaskText}
                                                        onChange={e => {
                                                            e.stopPropagation();
                                                            setEditingTaskText(e.target.value);
                                                        }}
                                                        onKeyDown={e => handleTaskTextKeyPress(e, task)}
                                                        onBlur={e => {
                                                            e.stopPropagation();
                                                            handleTaskTextSave(task);
                                                        }}
                                                        autoFocus
                                                        style={{ width: '100%' }}
                                                    />
                                                ) : (
                                                    <div
                                                        className={styles.taskText}
                                                        title={task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : ''}
                                                        onClick={e => handleTaskTextClick(e, task)}
                                                        style={{
                                                            textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            width: '100%',
                                                        }}>
                                                        {task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex">
                                            <div
                                                className={styles.starIcon}
                                                style={{ cursor: 'pointer', borderLeft: 'none' }}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onOpenSidebar(task);
                                                }}>
                                                <div className="hover-icon" style={{ display: 'none', width: '12px', height: '12px' }}>
                                                    <ArrowMaximize16Regular style={{ width: '12px', height: '12px', color: 'var(--Brand-Secondary)' }} />
                                                </div>
                                            </div>
                                            <div
                                                className={styles.starIcon}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleStarClick(e, task);
                                                }}
                                                style={{ cursor: 'pointer', borderLeft: 'none', opacity: task.starred ? 1 : 0, transition: 'opacity 0.2s ease' }}
                                                data-icon-type="star">
                                                {task.starred ? <Star16Filled style={{ color: '#FFD700' }} /> : <Star16Regular />}
                                            </div>
                                            <div
                                                className={styles.commentIcon}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onOpenSidebar(task);
                                                }}>
                                                {task?.comments_count > 0 ? <Comment16Filled color="var(--Brand-Secondary)" /> : <CommentAdd16Regular />}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            );
                        }
                        if (col === 'owner') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.owner }}>
                                    <UserAssignmentMenu task={task} onUserSelect={user => handleUserAssignment(task, user)}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            {(() => {
                                                const userInfo = getUserInfo(task.assigned_to);
                                                if (userInfo) {
                                                    return <CustomAvatar name={userInfo.full_name} size={20} />;
                                                }
                                                return <CustomAvatar name="" size={16} color="#6c757d" />;
                                            })()}
                                        </div>
                                    </UserAssignmentMenu>
                                </td>
                            );
                        }
                        if (col === 'status') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.status }}>
                                    <Menu>
                                        <MenuTrigger>
                                            <div
                                                className={styles.statusDiv}
                                                style={{
                                                    backgroundColor: getStatusInfo(task?.status)?.bg,
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                }}>
                                                {getStatusInfo(task?.status)?.text || task?.status}
                                            </div>
                                        </MenuTrigger>
                                        <MenuPopover style={{ width: '118px', minWidth: '118px' }}>
                                            <MenuList>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'COMPLETED')}
                                                    style={{ backgroundColor: 'var(--RAG-Green)', color: 'white' }}>
                                                    Completed
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'WORKING_ON_IT')}
                                                    style={{ backgroundColor: 'var(--RAG-Amber)', color: 'white' }}>
                                                    Working on it
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'STUCK')}
                                                    style={{ backgroundColor: 'var(--RAG-Red)', color: 'white' }}>
                                                    Stuck
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'NOT_STARTED')}
                                                    style={{ backgroundColor: '#C0C6D8', color: 'white' }}>
                                                    Not Started
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'CANCELLED')}
                                                    style={{ backgroundColor: '#6B6F89', color: 'white' }}>
                                                    Cancelled
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'ON_TRACK')}
                                                    style={{ backgroundColor: '#4F8A6F', color: 'white' }}>
                                                    On Track
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.statusMenuItem}
                                                    onClick={() => handleStatusChange(task, 'OFF_TRACK')}
                                                    style={{ backgroundColor: '#9E4F4F', color: 'white' }}>
                                                    Off Track
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                </td>
                            );
                        }
                        if (col === 'timeline') {
                            const start = formatTimelineDate(task?.start_date);
                            const end = formatTimelineDate(task?.end_date);
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.timeline }}>
                                    <TimelineRangePickerMenu task={task}>
                                        <div className="d-flex align-items-center justify-content-center px-2" style={{ height: '32px', width: '100%', cursor: 'pointer' }}>
                                            <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}>
                                                {start} - {end}
                                            </span>
                                        </div>
                                    </TimelineRangePickerMenu>
                                </td>
                            );
                        }
                        if (col === 'duration') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.duration }}>
                                    <div
                                        className="d-flex align-items-center justify-content-center px-2"
                                        style={{ height: '32px', width: '100%' }}>
                                        <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}>
                                            {formatDurationLabel(task.start_date, task.end_date)}
                                        </span>
                                    </div>
                                </td>
                            );
                        }
                        if (col === 'deadline') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.deadline }}>
                                    <DeadlinePickerMenu task={task} onDeadlineSelect={deadlineData => handleDeadlineSelect(task, deadlineData)}>
                                        <div className="d-flex align-items-center gap-1 px-2" style={{ cursor: 'pointer', width: '100%' }}>
                                            {(() => {
                                                const isCompleted = task.status === 'COMPLETED' || task.status === 'COMPLETE';
                                                const deadline = task.deadline ? new Date(task.deadline) : null;
                                                const today = new Date();
                                                // Normalize dates to midnight for accurate date-only comparison
                                                if (deadline) {
                                                    deadline.setHours(0, 0, 0, 0);
                                                }
                                                today.setHours(0, 0, 0, 0);
                                                
                                                const formattedDeadline = deadline
                                                    ? deadline.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                                                    : '-- --';

                                                return (
                                                    <>
                                                        <div className="d-flex align-items-center justify-content-center" style={{ width: '16px' }}>
                                                            {(() => {
                                                                if (!deadline || task.status === 'CANCELLED') {
                                                                    return null;
                                                                }

                                                                // Only show icon if deadline has passed (today > deadline)
                                                                const isOverdue = today > deadline;
                                                                
                                                                if (isOverdue && isCompleted) {
                                                                    return <Checkmark16Regular style={{ color: 'var(--RAG-Green)' }} />;
                                                                }

                                                                if (isOverdue && !isCompleted) {
                                                                    return <ErrorCircle16Regular style={{ color: 'var(--RAG-Red)' }} />;
                                                                }

                                                                return null;
                                                            })()}
                                                        </div>
                                                        <span
                                                            className={styles.tableRegularText}
                                                            style={{
                                                                textDecoration: isCompleted || task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                            }}>
                                                            {formattedDeadline}
                                                        </span>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </DeadlinePickerMenu>
                                </td>
                            );
                        }
                        if (col === 'priority') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.priority }}>
                                    <Menu>
                                        <MenuTrigger>
                                            {(() => {
                                                const priorityInfo = getPriorityInfo(task.priority);
                                                return (
                                                    <div
                                                        className={styles.statusDiv}
                                                        style={{
                                                            background: priorityInfo.bg,
                                                            cursor: 'pointer',
                                                        }}>
                                                        {priorityInfo.text || '-- --'}
                                                    </div>
                                                );
                                            })()}
                                        </MenuTrigger>
                                        <MenuPopover style={{ width: '117px', minWidth: '117px' }}>
                                            <MenuList>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, 'HIGH')}
                                                    style={{ backgroundColor: '#2B35AF', color: 'white' }}>
                                                    High
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, 'MEDIUM')}
                                                    style={{ backgroundColor: '#4361EE', color: 'white' }}>
                                                    Medium
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, 'LOW')}
                                                    style={{ backgroundColor: '#4895EF', color: 'white' }}>
                                                    Low
                                                </MenuItem>
                                                <MenuItem
                                                    className={styles.priorityMenuItem}
                                                    onClick={() => handlePriorityChange(task, '')}
                                                    style={{ backgroundColor: '#C0C6D8', color: 'white' }}>
                                                    None
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                </td>
                            );
                        }
                        if (col === 'workstream') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.workstream }}>
                                    <WorkstreamManagementMenu
                                        onWorkstreamSelect={workstream => handleWorkstreamSelect(task, workstream)}
                                        taskData={filteredData}
                                        selectedWorkstreamName={task.workstream_repr}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <span
                                                className={styles.tableRegularText}
                                                style={{
                                                    textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                }}>
                                                {task.workstream_repr || '-- --'}
                                            </span>
                                        </div>
                                    </WorkstreamManagementMenu>
                                </td>
                            );
                        }
                        if (col === 'tag') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.tag }}>
                                    <TagManagementMenu
                                        onTagSelect={tag => handleTagSelect(task, tag)}
                                        taskData={filteredData}
                                        taskWorkstreamName={task.workstream_repr}
                                        selectedTagName={
                                            Array.isArray(task.tags)
                                                ? task.tags.join(', ')
                                                : task.tag_repr
                                        }>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            {(() => {
                                                const hasTagsArray = Array.isArray(task.tags) && task.tags.length > 0;
                                                const tagsArray = hasTagsArray
                                                    ? task.tags
                                                    : task.tag_repr
                                                    ? [task.tag_repr]
                                                    : [];

                                                let displayLabel = '-- --';
                                                if (tagsArray.length > 0) {
                                                    const [first, ...rest] = tagsArray;
                                                    displayLabel = rest.length === 0 ? first : `${first}, +${rest.length}`;
                                                }

                                                return (
                                                    <Tooltip
                                                        relationship="description"
                                                        content={
                                                            tagsArray.length > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                    {tagsArray.map(tag => (
                                                                        <span key={tag}>{tag}</span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                '-- --'
                                                            )
                                                        }>
                                                        <span
                                                            className={styles.tableRegularText}
                                                            style={{
                                                                textDecoration: task.status === 'CANCELLED' ? 'line-through' : 'none',
                                                            }}>
                                                            {displayLabel}
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </div>
                                    </TagManagementMenu>
                                </td>
                            );
                        }
                        if (col === 'projectPhase') {
                            const commitProjectPhase = () => {
                                const nextValue = (projectPhaseDraft || '').trim();
                                if (handleProjectPhaseChange) {
                                    handleProjectPhaseChange(task, nextValue || null);
                                }
                                setIsEditingProjectPhase(false);
                            };

                            const cancelProjectPhase = () => {
                                setProjectPhaseDraft(task.project_phase || '');
                                setIsEditingProjectPhase(false);
                            };

                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.projectPhase }}>
                                    {isEditingProjectPhase ? (
                                        <input
                                            type="text"
                                            className={'form-control form-control-sm ' + styles.taskTextField}
                                            value={projectPhaseDraft}
                                            onChange={e => {
                                                e.stopPropagation();
                                                setProjectPhaseDraft(e.target.value);
                                            }}
                                            onKeyDown={e => {
                                                e.stopPropagation();
                                                if (e.key === 'Enter') {
                                                    commitProjectPhase();
                                                } else if (e.key === 'Escape') {
                                                    cancelProjectPhase();
                                                }
                                            }}
                                            onBlur={e => {
                                                e.stopPropagation();
                                                commitProjectPhase();
                                            }}
                                            style={{ width: '100%' }}
                                            autoFocus
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setProjectPhaseDraft(task.project_phase || '');
                                                setIsEditingProjectPhase(true);
                                            }}>
                                            <span className={styles.tableRegularText}>{task.project_phase || '-- --'}</span>
                                        </div>
                                    )}
                                </td>
                            );
                        }
                        if (col === 'lastUpdated') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.lastUpdated }}>
                                    <div className="d-flex align-items-center gap-2 px-2 justify-content-center">
                                        <div>
                                            <CustomAvatar name={task.last_updated_by_name} size={20} />
                                        </div>
                                        <span style={{ width: 60 }} className={styles.tableRegularText}>
                                            {formatLastUpdated(task.last_updated)}
                                        </span>
                                    </div>
                                </td>
                            );
                        }
                        if (col === 'addedOn') {
                            return (
                                <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.addedOn }}>
                                    <div className="d-flex align-items-center gap-2 px-2 justify-content-center">
                                        <div>
                                            <CustomAvatar name={task.created_by_name} size={20} />
                                        </div>
                                        <span style={{ width: 60 }} className={styles.tableRegularText}>
                                            {task.created ? new Date(task.created).toLocaleDateString() : '-- --'}
                                        </span>
                                    </div>
                                </td>
                            );
                        }
                        return null;
                    })}

                {/* Actions column */}
                <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <div className="menu-hover-icon" style={{ display: 'none' }}>
                            <Menu>
                                <MenuTrigger>
                                    <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MoreVertical16Regular style={{ width: '16px', height: '16px' }} />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem onClick={() => handleDelete(task)}>Delete</MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        </div>
                    </div>
                </td>
            </tr>

            {/* Render child tasks if expanded */}
            {isExpanded &&
                childTasks.map((childTask, childIndex) => (
                    <HierarchicalTaskRow
                        key={childTask.id}
                        task={childTask}
                        index={childIndex}
                        editingTaskId={editingTaskId}
                        setEditingTaskId={setEditingTaskId}
                        editingTaskText={editingTaskText}
                        setEditingTaskText={setEditingTaskText}
                        handleTaskTextClick={handleTaskTextClick}
                        handleTaskTextSave={handleTaskTextSave}
                        handleTaskTextCancel={handleTaskTextCancel}
                        handleTaskTextKeyPress={handleTaskTextKeyPress}
                        handleStarClick={handleStarClick}
                        handleUserAssignment={handleUserAssignment}
                        handleDeadlineSelect={handleDeadlineSelect}
                        handleStatusChange={handleStatusChange}
                        handlePriorityChange={handlePriorityChange}
                        handleTagSelect={handleTagSelect}
                        handleWorkstreamSelect={handleWorkstreamSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleSelectRow={handleSelectRow}
                        selectedRows={selectedRows}
                        isColumnVisible={isColumnVisible}
                        columnWidths={columnWidths}
                        columnOrder={columnOrder}
                        projectUsersAll={projectUsersAll}
                        filteredData={filteredData}
                        onOpenSidebar={onOpenSidebar}
                        getUserInfo={getUserInfo}
                        getStatusInfo={getStatusInfo}
                        getPriorityInfo={getPriorityInfo}
                        formatLastUpdated={formatLastUpdated}
                        formatDeadline={formatDeadline}
                        level={level + 1}
                        isExpanded={expandedTasks?.has ? expandedTasks.has(childTask.id) : false} // Use expandedTasks state
                        onToggleExpanded={onToggleExpanded}
                        onAddChildTask={onAddChildTask}
                        expandedTasks={expandedTasks}
                        handleProjectPhaseChange={handleProjectPhaseChange}
                    />
                ))}

            {/* Empty row for adding new child tasks with existing background color */}
            {isExpanded && canHaveChildren && !childTasks.some(child => (expandedTasks?.has ? expandedTasks.has(child.id) : false)) && (
                <tr className={styles.tableRow} style={{ backgroundColor: '#F3FDFE' }}>
                    <td className={styles.tableRowItem} style={{ width: columnWidths.checkbox }}>
                        <input className={styles.checkboxTable} type="checkbox" disabled style={{ opacity: 0.5 }} />
                    </td>
                    {(columnOrder || ['task', 'owner', 'status', 'timeline', 'deadline', 'duration', 'priority', 'workstream', 'tag', 'projectPhase', 'lastUpdated', 'addedOn'])
                        .filter(col => isColumnVisible(col))
                        .map(col => {
                            if (col === 'task') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.task }}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center" style={{ paddingLeft: getIndentation() + 24 }}>
                                                <div className="me-2" style={{ minWidth: '16px', marginLeft: '8px' }}></div>
                                                <div className="flex-fill">
                                                    <input
                                                        type="text"
                                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                                        placeholder={level === 0 ? 'Add new subtask' : 'Add new action item'}
                                                        autoFocus
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') {
                                                                if (e.target.value.trim()) {
                                                                    onAddChildTask(task.id, e.target.value.trim());
                                                                    e.target.value = '';
                                                                }
                                                            } else if (e.key === 'Escape') {
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        onBlur={e => {
                                                            if (e.target.value.trim()) {
                                                                onAddChildTask(task.id, e.target.value.trim());
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex">
                                                <div className={styles.starIcon} style={{ opacity: 0.5 }}>
                                                    <Star16Regular />
                                                </div>
                                                <div className={styles.commentIcon} style={{ opacity: 0.5 }}>
                                                    <CommentAdd16Regular />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'owner') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.owner }}>
                                        <div style={{ width: '100%', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CustomAvatar name="" size={16} color="#6c757d" />
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'status') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.status }}>
                                        <div className={styles.statusDiv} style={{ backgroundColor: '#C0C6D8', color: 'white', opacity: 0.5 }}>
                                            Not Started
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'timeline') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.timeline }}>
                                        <div className="d-flex align-items-center justify-content-center px-2" style={{ height: '32px', opacity: 0.5, width: '100%' }}>
                                            <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}>
                                                -- -- - -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'deadline') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.deadline }}>
                                        <div className="d-flex align-items-center gap-1 px-2">
                                            <div className="d-flex align-items-center justify-content-center" style={{ width: '16px' }}></div>
                                            <span className={styles.tableRegularText} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'duration') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.duration }}>
                                        <div className="d-flex align-items-center gap-1 px-2">
                                            <span className={styles.tableRegularText} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'priority') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.priority }}>
                                        <div className={styles.statusDiv} style={{ background: '#C0C6D8', opacity: 0.5 }}>
                                            None
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'workstream') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.workstream }}>
                                        <div className=" gap-1 px-2">
                                            <span className={styles.tableRegularText + ' text-center'} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'tag') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.tag }}>
                                        <div className=" gap-1 px-2">
                                            <span className={styles.tableRegularText} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'projectPhase') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.projectPhase }}>
                                        <div className=" gap-1 px-2">
                                            <span className={styles.tableRegularText + ' text-center'} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'lastUpdated') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.lastUpdated }}>
                                        <div className="d-flex align-items-center gap-2 px-2">
                                            <span className={styles.tableRegularText + ' m-auto'} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            if (col === 'addedOn') {
                                return (
                                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.addedOn }}>
                                        <div className="d-flex align-items-center gap-2 px-2">
                                            <span className={styles.tableRegularText + ' m-auto'} style={{ opacity: 0.5 }}>
                                                -- --
                                            </span>
                                        </div>
                                    </td>
                                );
                            }
                            return null;
                        })}
                    <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MoreVertical16Regular style={{ opacity: 0.5 }} />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default HierarchicalTaskRow;
